import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link2,
  Loader2,
  Play,
  RotateCcw,
  Trash2,
  X,
  Sparkles,
  Info,
  Wand2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { api, formatApiError } from "../lib/api";

const INTERVAL_CHOICES = [1, 6, 12, 24];

/**
 * Reusable feed-connect panel. Used by both Vendi (dealer-scoped endpoints)
 * and Admin (per-dealer endpoints). Handles: fetch current feed, edit fields
 * (URL, format, interval, field mapping), enable toggle, save, delete.
 * The "Sincronizza ora" trigger lives on the status card, not here.
 */
export default function StockFeedPanel({
  scope = "dealer", // "dealer" | "admin"
  dealerId, // required if scope="admin"
  onChanged,
  demoBackendBase, // e.g. process.env.REACT_APP_BACKEND_URL
}) {
  const { t } = useTranslation();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const [form, setForm] = useState({
    url: "",
    format: "csv",
    interval_hours: 6,
    enabled: true,
    field_mapping: {},
    import_filters: { price_min: "", price_max: "", brands: "", only_available: false },
  });
  const [detecting, setDetecting] = useState(false);
  const [detectMsg, setDetectMsg] = useState(null); // {kind:'ok'|'warn'|'err', text}

  const feedUrl =
    scope === "admin"
      ? `/admin/dealers/${dealerId}/stock-feed`
      : `/dealers/me/stock-feed`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r =
        scope === "admin"
          ? await api.get(`/admin/stock-feeds`).then((res) => {
              const it = (res.data?.items || []).find(
                (x) => x.dealer_id === dealerId
              );
              return { data: it || null };
            })
          : await api.get(feedUrl);
      if (r.data) {
        setFeed(r.data);
        const filt = r.data.import_filters || {};
        setForm({
          url: r.data.url || "",
          format: r.data.format || "csv",
          interval_hours: r.data.interval_hours || 6,
          enabled: r.data.enabled !== false,
          field_mapping: r.data.field_mapping || {},
          import_filters: {
            price_min: filt.price_min ?? "",
            price_max: filt.price_max ?? "",
            brands: Array.isArray(filt.brands) ? filt.brands.join(", ") : (filt.brands || ""),
            only_available: !!filt.only_available,
          },
        });
      } else {
        setFeed(null);
      }
    } catch (e) {
      // 404 (no feed yet) is not an error for us
      setFeed(null);
    } finally {
      setLoading(false);
    }
  }, [scope, dealerId, feedUrl]);

  // Fetch default mapping whenever format changes and mapping is empty.
  useEffect(() => {
    if (Object.keys(form.field_mapping || {}).length > 0) return;
    api
      .get(`/stock-feed/defaults?fmt=${form.format}`)
      .then((r) => setForm((s) => ({ ...s, field_mapping: r.data.field_mapping || {} })))
      .catch(() => {});
  }, [form.format, form.field_mapping]);

  useEffect(() => {
    load();
  }, [load]);

  const useDemo = () => {
    const base = demoBackendBase || "";
    const path = form.format === "xml" ? "/api/demo-feeds/stock.xml" : "/api/demo-feeds/stock.csv";
    setForm((s) => ({ ...s, url: `${base}${path}` }));
    setDetectMsg(null);
  };

  const useDemoDe = () => {
    const base = demoBackendBase || "";
    setForm((s) => ({ ...s, format: "csv", url: `${base}/api/demo-feeds/stock-de.csv`, field_mapping: {} }));
    setDetectMsg(null);
  };

  const autoDetect = async () => {
    if (!form.url.trim()) {
      setDetectMsg({ kind: "err", text: t("stock_feed.auto_detect_url_required") });
      return;
    }
    setDetecting(true);
    setDetectMsg(null);
    try {
      const r = await api.post("/stock-feed/detect-mapping", {
        url: form.url.trim(),
        format: form.format,
      });
      const suggested = r.data.suggested_mapping || {};
      const detected = r.data.detected || {};
      const unmapped = r.data.unmapped || [];
      setForm((s) => ({ ...s, field_mapping: { ...s.field_mapping, ...suggested } }));
      const detectedCount = Object.keys(detected).length;
      if (unmapped.length > 0) {
        setDetectMsg({
          kind: "warn",
          text: `${t("stock_feed.auto_detect_success", { n: detectedCount })} · ${t("stock_feed.auto_detect_unmapped", { list: unmapped.join(", ") })}`,
        });
      } else {
        setDetectMsg({ kind: "ok", text: t("stock_feed.auto_detect_success", { n: detectedCount }) });
      }
    } catch (e) {
      setDetectMsg({ kind: "err", text: t("stock_feed.auto_detect_failed") });
    } finally {
      setDetecting(false);
    }
  };

  const resetMapping = async () => {
    const r = await api.get(`/stock-feed/defaults?fmt=${form.format}`);
    setForm((s) => ({ ...s, field_mapping: r.data.field_mapping || {} }));
  };

  const setMappingCell = (field, value) => {
    setForm((s) => ({
      ...s,
      field_mapping: { ...s.field_mapping, [field]: value },
    }));
  };

  const save = async () => {
    if (!form.url.trim()) {
      setErr(t("stock_feed.err_url_required"));
      return;
    }
    setErr(null);
    setSaving(true);
    try {
      // Serialize import_filters: strip empty strings, split brands to list.
      const rawIf = form.import_filters || {};
      const brandsList = String(rawIf.brands || "")
        .split(/[,;\n]+/g)
        .map((s) => s.trim())
        .filter(Boolean);
      const importFilters = {
        price_min: rawIf.price_min === "" || rawIf.price_min == null ? null : Number(rawIf.price_min),
        price_max: rawIf.price_max === "" || rawIf.price_max == null ? null : Number(rawIf.price_max),
        brands: brandsList,
        only_available: !!rawIf.only_available,
      };
      const payload = {
        url: form.url,
        format: form.format,
        interval_hours: form.interval_hours,
        enabled: form.enabled,
        field_mapping: form.field_mapping,
        import_filters: importFilters,
      };
      const r = await api.put(feedUrl, payload);
      setFeed(r.data);
      if (onChanged) onChanged(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("stock_feed.confirm_delete"))) return;
    await api.delete(feedUrl);
    setFeed(null);
    setForm({
      url: "",
      format: "csv",
      interval_hours: 6,
      enabled: true,
      field_mapping: {},
      import_filters: { price_min: "", price_max: "", brands: "", only_available: false },
    });
    if (onChanged) onChanged(null);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", "Caricamento…")}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white p-6"
      data-testid={`stock-feed-panel-${scope}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
          <Link2 className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("stock_feed.overline")}
          </p>
          <h2 className="mt-0.5 font-heading font-semibold text-lg tracking-tight text-slate-950">
            {t("stock_feed.title")}
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
            {t("stock_feed.subtitle")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-2">
          <Label className="text-xs font-medium text-slate-600">{t("stock_feed.url_label")}</Label>
          <Input
            data-testid="stock-feed-url-input"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://…/stock.xml"
            className="mt-1 h-10 rounded-lg"
          />
          <button
            type="button"
            onClick={useDemo}
            data-testid="stock-feed-use-demo"
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700"
          >
            <Sparkles className="w-3 h-3" />
            {t("stock_feed.use_demo")}
          </button>
          <span className="mx-1.5 text-[11px] text-slate-300">·</span>
          <button
            type="button"
            onClick={useDemoDe}
            data-testid="stock-feed-use-demo-de"
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700"
          >
            <Sparkles className="w-3 h-3" />
            {t("stock_feed.use_demo_de")}
          </button>
        </div>
        <div>
          <Label className="text-xs font-medium text-slate-600">{t("stock_feed.format_label")}</Label>
          <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v, field_mapping: {} })}>
            <SelectTrigger data-testid="stock-feed-format-select" className="mt-1 h-10 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="xml">XML</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div>
          <Label className="text-xs font-medium text-slate-600">{t("stock_feed.interval_label")}</Label>
          <Select
            value={String(form.interval_hours)}
            onValueChange={(v) => setForm({ ...form, interval_hours: parseInt(v, 10) })}
          >
            <SelectTrigger data-testid="stock-feed-interval-select" className="mt-1 h-10 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTERVAL_CHOICES.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {t("stock_feed.interval_hours", { count: h })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 flex items-end">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <Switch
              data-testid="stock-feed-enabled-switch"
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
            {form.enabled ? t("stock_feed.enabled") : t("stock_feed.disabled")}
          </label>
        </div>
      </div>

      {/* Field mapping */}
      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold text-sm tracking-tight text-slate-950">
              {t("stock_feed.mapping_title")}
            </h3>
            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Info className="w-3 h-3" /> {t("stock_feed.mapping_hint")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={autoDetect}
              disabled={detecting}
              data-testid="stock-feed-mapping-auto-detect"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-60"
            >
              {detecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              {detecting ? t("stock_feed.auto_detect_running") : t("stock_feed.auto_detect_btn")}
            </button>
            <button
              type="button"
              onClick={resetMapping}
              data-testid="stock-feed-mapping-reset"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900"
            >
              <RotateCcw className="w-3 h-3" /> {t("stock_feed.reset_mapping")}
            </button>
          </div>
        </div>
        {detectMsg && (
          <div
            data-testid="stock-feed-mapping-detect-msg"
            className={`mb-3 text-[11px] rounded-lg px-3 py-2 border ${
              detectMsg.kind === "ok"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : detectMsg.kind === "warn"
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            {detectMsg.text}
          </div>
        )}
        <div
          data-testid="stock-feed-mapping-table"
          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
        >
          {Object.entries(form.field_mapping || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([field, value]) => (
              <div key={field} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 w-28 shrink-0">
                  {field}
                </div>
                <Input
                  data-testid={`stock-feed-mapping-input-${field}`}
                  value={value}
                  onChange={(e) => setMappingCell(field, e.target.value)}
                  className="h-8 text-xs rounded-md bg-white"
                />
              </div>
            ))}
        </div>
      </div>

      {/* Import filters */}
      <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50/60 p-4" data-testid="stock-feed-filters">
        <div className="flex items-baseline justify-between gap-2 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {t("stock_feed.filters_title")}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">{t("stock_feed.filters_hint")}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="sf-fmin" className="text-[11px] font-medium text-slate-600">
              {t("stock_feed.filter_price_min")}
            </Label>
            <Input
              id="sf-fmin"
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={form.import_filters.price_min}
              onChange={(e) =>
                setForm((s) => ({ ...s, import_filters: { ...s.import_filters, price_min: e.target.value } }))
              }
              data-testid="stock-feed-filter-price-min"
              className="mt-1 h-9 rounded-lg text-xs"
            />
          </div>
          <div>
            <Label htmlFor="sf-fmax" className="text-[11px] font-medium text-slate-600">
              {t("stock_feed.filter_price_max")}
            </Label>
            <Input
              id="sf-fmax"
              type="number"
              inputMode="numeric"
              placeholder="—"
              value={form.import_filters.price_max}
              onChange={(e) =>
                setForm((s) => ({ ...s, import_filters: { ...s.import_filters, price_max: e.target.value } }))
              }
              data-testid="stock-feed-filter-price-max"
              className="mt-1 h-9 rounded-lg text-xs"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="sf-brands" className="text-[11px] font-medium text-slate-600">
              {t("stock_feed.filter_brands")}
            </Label>
            <Input
              id="sf-brands"
              placeholder={t("stock_feed.filter_brands_placeholder")}
              value={form.import_filters.brands}
              onChange={(e) =>
                setForm((s) => ({ ...s, import_filters: { ...s.import_filters, brands: e.target.value } }))
              }
              data-testid="stock-feed-filter-brands"
              className="mt-1 h-9 rounded-lg text-xs"
            />
            <p className="text-[10px] text-slate-400 mt-1">{t("stock_feed.filter_brands_hint")}</p>
          </div>
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-[12px] text-slate-700">
            <Switch
              data-testid="stock-feed-filter-only-available"
              checked={!!form.import_filters.only_available}
              onCheckedChange={(v) =>
                setForm((s) => ({ ...s, import_filters: { ...s.import_filters, only_available: !!v } }))
              }
            />
            {t("stock_feed.filter_only_available")}
          </label>
        </div>
      </div>

      {err && (
        <div
          data-testid="stock-feed-error"
          className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {err}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            data-testid="stock-feed-save"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-blue-600 hover:bg-blue-700 text-white h-10 px-5 text-sm font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-3.5 h-3.5 mr-2" />}
            {feed ? t("stock_feed.save_changes") : t("stock_feed.connect_feed")}
          </Button>
          {feed && (
            <Button
              type="button"
              data-testid="stock-feed-delete"
              onClick={remove}
              variant="outline"
              className="rounded-full h-10 px-4 text-sm font-semibold text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              {t("stock_feed.delete")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
